import React, { useMemo, useState } from 'react';
import { X, Settings, ChevronDown, ChevronUp, Search, Calendar, User, Phone, Mail, Clock, Activity } from 'lucide-react';
import { Lead, allLeads } from '../utils/leadMockData';
import { CampaignData } from '../utils/mockData';
interface LeadDetailsModalProps {
  campaign: CampaignData;
  isOpen: boolean;
  onClose: () => void;
}
type ColumnKey = keyof Lead | 'actions';
interface ColumnConfig {
  key: ColumnKey;
  label: string;
  defaultVisible: boolean;
  sortable: boolean;
}
const COLUMNS: ColumnConfig[] = [{
  key: 'name',
  label: 'Name',
  defaultVisible: true,
  sortable: true
}, {
  key: 'email',
  label: 'Email',
  defaultVisible: true,
  sortable: true
}, {
  key: 'phone',
  label: 'Phone',
  defaultVisible: false,
  sortable: false
}, {
  key: 'currentStage',
  label: 'Current Funnel Stage',
  defaultVisible: true,
  sortable: true
}, {
  key: 'status',
  label: 'Status',
  defaultVisible: true,
  sortable: true
}, {
  key: 'daysInStage',
  label: 'Days in Stage',
  defaultVisible: true,
  sortable: true
}, {
  key: 'loanAmount',
  label: 'Loan Amount',
  defaultVisible: false,
  sortable: true
}, {
  key: 'loanType',
  label: 'Loan Type',
  defaultVisible: false,
  sortable: true
}, {
  key: 'currentInterestRate',
  label: 'Interest Rate',
  defaultVisible: false,
  sortable: true
}, {
  key: 'previousClosingDate',
  label: 'Previous Closing',
  defaultVisible: false,
  sortable: true
}, {
  key: 'loanOfficer',
  label: 'Loan Officer',
  defaultVisible: false,
  sortable: true
}, {
  key: 'lastActivity',
  label: 'Last Activity',
  defaultVisible: false,
  sortable: true
}, {
  key: 'creditPullDate',
  label: 'Credit Pull',
  defaultVisible: false,
  sortable: true
}, {
  key: 'applicationDate',
  label: 'Application',
  defaultVisible: false,
  sortable: true
}, {
  key: 'submissionDate',
  label: 'Submission',
  defaultVisible: false,
  sortable: true
}, {
  key: 'fundingDate',
  label: 'Funding',
  defaultVisible: false,
  sortable: true
}];
export function LeadDetailsModal({
  campaign,
  isOpen,
  onClose
}: LeadDetailsModalProps) {
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(new Set(COLUMNS.filter(c => c.defaultVisible).map(c => c.key)));
  const [showColumnCustomizer, setShowColumnCustomizer] = useState(false);
  const [sortConfig, setSortConfig] = useState<{
    key: ColumnKey;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const campaignLeads = useMemo(() => allLeads.filter(lead => lead.campaignId === campaign.id), [campaign.id]);
  const filteredAndSortedLeads = useMemo(() => {
    let result = [...campaignLeads];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(lead => lead.name.toLowerCase().includes(query) || lead.email.toLowerCase().includes(query) || lead.loanOfficer.toLowerCase().includes(query));
    }
    if (sortConfig) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key as keyof Lead];
        const bValue = b[sortConfig.key as keyof Lead];
        if (aValue === bValue) return 0;
        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;
        const comparison = aValue < bValue ? -1 : 1;
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }
    return result;
  }, [campaignLeads, searchQuery, sortConfig]);
  if (!isOpen) return null;
  const toggleColumn = (key: ColumnKey) => {
    const newSet = new Set(visibleColumns);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    setVisibleColumns(newSet);
  };
  const handleSort = (key: ColumnKey) => {
    setSortConfig(current => {
      if (current?.key === key) {
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return {
        key,
        direction: 'asc'
      };
    });
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Converted':
        return 'bg-emerald-100 text-emerald-700';
      case 'Dropped':
        return 'bg-rose-100 text-rose-700';
      default:
        return 'bg-blue-100 text-blue-700';
    }
  };
  const formatCurrency = (amount?: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-45">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity" onClick={onClose} aria-hidden="true" data-id="element-46" />

      <div className="relative w-full max-w-6xl max-h-[90vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-47">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white z-10" data-id="element-48">
          <div data-id="element-49">
            <h2 id="modal-title" className="text-xl font-bold text-slate-900" data-id="element-50">
              {campaign.name}
            </h2>
            <p className="text-sm text-slate-500 mt-1" data-id="element-51">
              Viewing {filteredAndSortedLeads.length} leads
            </p>
          </div>

          <div className="flex items-center gap-3" data-id="element-52">
            <div className="relative" data-id="element-53">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} data-id="element-54" />
              <input type="text" placeholder="Search leads..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64" data-id="element-55" />
            </div>

            <div className="relative" data-id="element-56">
              <button onClick={() => setShowColumnCustomizer(!showColumnCustomizer)} className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${showColumnCustomizer ? 'bg-slate-100 border-slate-300 text-slate-900' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-57">
                <Settings size={16} data-id="element-58" />
                Columns
              </button>

              {/* Column Customizer Dropdown */}
              {showColumnCustomizer && <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-lg shadow-xl border border-slate-200 p-4 z-50" data-id="element-59">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3" data-id="element-60">
                    Visible Columns
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto" data-id="element-61">
                    {COLUMNS.map(col => <label key={col.key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:bg-slate-50 p-1 rounded" data-id="element-62">
                        <input type="checkbox" checked={visibleColumns.has(col.key)} onChange={() => toggleColumn(col.key)} disabled={col.key === 'name'} // Name always required
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" data-id="element-63" />
                        {col.label}
                      </label>)}
                  </div>
                </div>}
            </div>

            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" aria-label="Close modal" data-id="element-64">
              <X size={20} data-id="element-65" />
            </button>
          </div>
        </div>

        {/* Table Content with Horizontal Scroll */}
        <div className="flex-1 overflow-auto bg-slate-50 p-6" data-id="element-66">
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-id="element-67">
            <div className="overflow-x-auto" data-id="element-68">
              <table className="w-full text-left text-sm" data-id="element-69">
                <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-70">
                  <tr data-id="element-71">
                    {COLUMNS.filter(col => visibleColumns.has(col.key)).map((col, index) => <th key={col.key} className={`px-6 py-4 whitespace-nowrap ${col.sortable ? 'cursor-pointer hover:bg-slate-100' : ''} ${col.key === 'name' ? 'sticky left-0 z-20 bg-slate-50 after:absolute after:right-0 after:top-0 after:bottom-0 after:w-px after:bg-slate-200' : ''}`} onClick={() => col.sortable && handleSort(col.key)} data-id="element-72">
                          <div className="flex items-center gap-1" data-id="element-73">
                            {col.label}
                            {sortConfig?.key === col.key && (sortConfig.direction === 'asc' ? <ChevronUp size={14} data-id="element-74" /> : <ChevronDown size={14} data-id="element-75" />)}
                          </div>
                        </th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white" data-id="element-76">
                  {filteredAndSortedLeads.map(lead => <tr key={lead.id} className="hover:bg-slate-50 transition-colors group" data-id="element-77">
                      {COLUMNS.filter(col => visibleColumns.has(col.key)).map((col, index) => <td key={`${lead.id}-${col.key}`} className={`px-6 py-4 whitespace-nowrap ${col.key === 'name' ? 'sticky left-0 z-10 bg-white group-hover:bg-slate-50 after:absolute after:right-0 after:top-0 after:bottom-0 after:w-px after:bg-slate-200' : ''}`} data-id="element-78">
                            {col.key === 'name' && <div className="flex items-center gap-3" data-id="element-79">
                                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-medium text-xs flex-shrink-0" data-id="element-80">
                                  {lead.name.split(' ').map(n => n[0]).join('')}
                                </div>
                                <div className="min-w-[150px]" data-id="element-81">
                                  <div className="font-medium text-slate-900" data-id="element-82">
                                    {lead.name}
                                  </div>
                                </div>
                              </div>}

                            {col.key === 'status' && <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`} data-id="element-83">
                                {lead.status}
                              </span>}

                            {col.key === 'currentStage' && <span className="text-slate-700 font-medium" data-id="element-84">
                                {lead.currentStage}
                              </span>}

                            {col.key === 'daysInStage' && <span className={`font-medium ${lead.daysInStage > 14 ? 'text-amber-600' : 'text-slate-600'}`} data-id="element-85">
                                {lead.daysInStage} days
                              </span>}

                            {col.key === 'loanAmount' && <span className="text-slate-900 font-medium" data-id="element-86">
                                {formatCurrency(lead.loanAmount)}
                              </span>}

                            {col.key === 'loanType' && <span className="text-slate-700" data-id="element-87">
                                {lead.loanType || '-'}
                              </span>}

                            {col.key === 'currentInterestRate' && <span className="text-slate-700 font-medium" data-id="element-88">
                                {lead.currentInterestRate || '-'}
                              </span>}

                            {col.key === 'previousClosingDate' && <span className="text-slate-600" data-id="element-89">
                                {lead.previousClosingDate || '-'}
                              </span>}

                            {/* Generic render for other fields */}
                            {['phone', 'loanOfficer', 'lastActivity', 'creditPullDate', 'applicationDate', 'submissionDate', 'fundingDate'].includes(col.key as string) && <span className="text-slate-600" data-id="element-90">
                                {lead[col.key as keyof Lead] || '-'}
                              </span>}

                            {/* Email column */}
                            {col.key === 'email' && <span className="text-slate-600" data-id="element-91">
                                {lead.email}
                              </span>}
                          </td>)}
                    </tr>)}
                </tbody>
              </table>
            </div>

            {filteredAndSortedLeads.length === 0 && <div className="p-12 text-center text-slate-500" data-id="element-92">
                No leads found matching your search.
              </div>}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 bg-white flex justify-between items-center text-sm text-slate-500" data-id="element-93">
          <div data-id="element-94">
            Showing {filteredAndSortedLeads.length} of {campaignLeads.length}{' '}
            leads
          </div>
          <div className="flex gap-2" data-id="element-95">
            <button className="px-3 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50" disabled data-id="element-96">
              Previous
            </button>
            <button className="px-3 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50" disabled data-id="element-97">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>;
}